import type { TutorialStep } from "./TutorialOverlay";

// Post-registration intro tour. Walks the player from a fresh account all the
// way through their FIRST Castle upgrade (Lvl 1 → 2), then hands off to the
// onboarding quest chain which continues to Lvl 10.
//
// Step layout:
//   1. Welcome (center modal, element flavor)
//   2. ResourceBar at top
//   3. Spotlight the Castle building tile — instruct to tap it
//   4. After tap: BuildingDetailSheet opens at z-[60] above the tutorial dim;
//      tutorial step says "press Строить → Lvl 2" (center modal, since the
//      sheet covers the spotlight layer while open).
//   5. Acknowledgement: castle is building, ~3 min wait
//   6. Buildings list overview (gradual reveal explanation)
//   7. Bottom navigation
//   8. Quests tab
//   9. Final
//
// Copy is written for someone who has never played a strategy game before —
// no jargon (PvE / march / rally / gear / tier / DPS), only plain Russian
// describing what the button does and why the player would tap it.
export function buildTutorialSteps(
  displayName: string | null | undefined,
  elementId: number,
): TutorialStep[] {
  const name = (displayName ?? "маг").trim() || "маг";
  const ElementGreeting: Record<number, string> = {
    0: "Огонь — твоя стихия. У тебя будет огненное королевство с фениксом, пиромантами и сильными атакующими воинами.",
    1: "Вода — твоя стихия. У тебя будет морское королевство с русалкой-целителем и крепкими защитниками.",
    2: "Воздух — твоя стихия. У тебя будет небесное королевство с грифонами и быстрыми воинами-молниями.",
    3: "Земля — твоя стихия. У тебя будет каменное королевство с мифриловыми титанами и крепкими защитниками.",
  };

  return [
    {
      id: "welcome",
      title: `Здравствуй, ${name}`,
      body:
        (ElementGreeting[elementId] ?? "У тебя будет королевство твоей стихии.") +
        "\n\nДавай за пару минут разберёмся как тут всё устроено — и сразу построим первый апгрейд.",
    },
    {
      id: "resources",
      title: "Это твои ресурсы",
      body:
        "Наверху четыре полоски: золото, дерево, камень и мана. Это то, что ты тратишь на стройку и армию. Ресурсы копятся сами, пока работают добывающие здания (шахта, лесопилка, каменоломня, алтарь маны). Чем выше уровень здания — тем быстрее копятся.",
      targetSelector: '[data-tutorial="resource-bar"]',
      switchToTab: "castle",
    },
    {
      id: "tap-castle",
      title: "Это твой Замок",
      body:
        "Главное здание королевства. Уровень Замка ограничивает все остальные постройки и открывает новые. Тапни по нему — откроется окно с деталями. После тапа возвращайся сюда и нажми «Дальше».",
      targetSelector: '[data-tutorial="building-castle"]',
      switchToTab: "castle",
    },
    {
      id: "upgrade-castle",
      title: "Запусти улучшение до 2 уровня",
      body:
        "В открывшемся окне ты увидишь стоимость улучшения и сколько времени займёт стройка. Стартовых ресурсов у тебя хватает ровно на это — нажми «Строить → Lvl 2» внизу окна. После этого закрывай окно и нажми «Дальше».",
    },
    {
      id: "castle-building",
      title: "Замок строится",
      body:
        "Готово. Замок поднимется до 2 уровня примерно через 3 минуты. Пока ждёшь — ресурсы не стоят на месте, шахты продолжают копить золото и камень. Дальше я покажу остальное.",
    },
    {
      id: "buildings",
      title: "Остальные твои здания",
      body:
        "Под Замком — список твоих зданий. Сейчас открыты только базовые (Замок и шахты). Когда Замок поднимется на следующий уровень — откроются новые: Алтарь маны, Хранилище, Хижина строителя. На 3 уровне появятся Казармы и Зал войны. Так постепенно весь город и развернётся.",
      targetSelector: '[data-tutorial="buildings-list"]',
      switchToTab: "castle",
    },
    {
      id: "nav",
      title: "Меню снизу",
      body:
        "Сейчас три раздела: Замок (ты здесь), Профиль (ты, рейтинг, события) и Задания. Когда построишь Казармы — появится раздел «Армия» (там готовишь воинов). Когда построишь Зал войны — появится «Походы» (там отправляешь воинов сражаться). Альянс (объединение игроков) откроется когда вырастешь — на уровне Замка 10.",
      targetSelector: '[data-tutorial="bottom-nav"]',
      switchToTab: "castle",
    },
    {
      id: "quests",
      title: "Задания и сундуки",
      body:
        "Здесь твоя дорожная карта. Задания для новичка ведут тебя от 2 до 10 уровня Замка — за каждое выполненное достижение выдают ресурсы и кристаллы для героев. Ежедневные обновляются каждый день в полночь. И самое важное — сверху висит «Сундук новичка»: каждые 10 минут даёт ресурсы, пока ты не вышел из новичка. Заходи сюда после каждого важного действия.",
      targetSelector: '[data-tutorial="quest-tab"]',
      switchToTab: "quests",
    },
    {
      id: "final",
      title: "Готово, можешь начинать",
      body:
        "На 2 дня у тебя есть щит — пока он висит, никто не нападёт. За это время спокойно подними Замок до 10 уровня по цепочке заданий — выйдешь из новичка с армией, героями и альянсом. Если запутаешься — напиши боту в чат, я подскажу. Удачи.",
    },
  ];
}
